"use client";

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type LogRow = {
  id: number;
  user_id: number | null;
  user_name: string | null;
  action: string;
  module: string;
  record_id: string | null;
  created_at: string | null;
};

export default function AdminAuditPage() {
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [module, setModule] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "audit", userId, action, module, page],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, per_page: 25 };
      if (userId.trim()) {
        params.user_id = Number(userId);
      }
      if (action.trim()) {
        params.action = action.trim();
      }
      if (module.trim()) {
        params.module = module.trim();
      }
      const { data: d } = await api.get<{ logs: LogRow[]; pagination: { total: number; last_page: number } }>(
        "/api/audit-logs",
        { params },
      );
      return d;
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="text-sm text-muted-foreground">Filter by user, action, module.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid gap-2 w-[120px]">
            <Label>User id</Label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} />
          </div>
          <div className="grid gap-2 w-[140px]">
            <Label>Action</Label>
            <Input placeholder="update" value={action} onChange={(e) => setAction(e.target.value)} />
          </div>
          <div className="grid gap-2 w-[140px]">
            <Label>Module</Label>
            <Input placeholder="settings" value={module} onChange={(e) => setModule(e.target.value)} />
          </div>
          <Button type="button" variant="secondary" onClick={() => void refetch()}>
            Apply
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Events</CardTitle>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Prev
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!data || page >= (data.pagination?.last_page ?? 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Module</th>
                  <th className="py-2">Record</th>
                </tr>
              </thead>
              <tbody>
                {(data?.logs ?? []).map((r) => (
                  <tr key={r.id} className="border-b border-border/60">
                    <td className="py-2 pr-3 whitespace-nowrap">{r.created_at ?? "—"}</td>
                    <td className="py-2 pr-3">{r.user_name ?? r.user_id ?? "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.action}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.module}</td>
                    <td className="py-2 font-mono text-xs">{r.record_id ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
